import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';

import { environment } from 'environments/environment';

const { apiUrl } = environment;

export const ROL_ADMIN = 'ADMIN';
export const ROL_SUPERVISOR = 'SUPERVISOR';
export const ROL_AUDITOR = 'AUDITOR';

type Rol = {
  clave: string;
}

type User = {
  id: string;
  nombre: string;
  email: string;
  roles: Rol[];
  puestoAsignado: boolean;
  requiereCambioPass: boolean;
}

interface ApiJwtLoginResponse {
    token: string;
    usuario: User;
}

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        // return this._httpClient.post('api/auth/forgot-password', email);
        return throwError('No implementado');
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<void>
    {
      return this._httpClient.post<void>(`${apiUrl}/auth/reset-password`, {
          password,
      });
    }

    getUser(): Observable<User>
    {
        return this._httpClient.post<ApiJwtLoginResponse>(`${apiUrl}/auth/refresh-token`, {
            accessToken: this.accessToken,
        }).pipe(
          map(response => response.usuario)
        );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<ApiJwtLoginResponse>
    {
        return this._httpClient.post<ApiJwtLoginResponse>(`${apiUrl}/auth/login`, credentials).pipe(
            switchMap((response) =>
            {
                // Store the access token in the local storage
                this.accessToken = response.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = {
                  id: response.usuario.id,
                  name: response.usuario.nombre,
                  email: response.usuario.email
                };

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post<ApiJwtLoginResponse>(`${apiUrl}/auth/refresh-token`, {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: ApiJwtLoginResponse) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.token )
                {
                    this.accessToken = response.token;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = {
                  id: response?.usuario?.id,
                  name: response?.usuario?.nombre,
                  email: response?.usuario?.email
                };

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
